import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, combineLatest, filter, map, take } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { SelectOption } from '@fcom/common';
import { stopPropagation } from '@fcom/core/utils';

import { IconButtonSize, IconButtonTheme } from '../../buttons';
import { SelectInputComponent } from '../select-input/select-input.component';
import { TextInputComponent } from '../text-input/text-input.component';
import { CursorType } from '../enums';
import { NotificationTheme } from '../../notifications';

@Component({
  selector: 'fin-combo-box',
  templateUrl: './combo-box.component.html',
  styleUrls: ['./combo-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ComboBoxComponent extends SelectInputComponent implements AfterViewInit {
  private fb = inject(UntypedFormBuilder);

  readonly CursorType = CursorType;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input()
  searchable = false;

  @Input()
  searchLabel: string;

  @Input()
  clearSearchLabel: string;

  @Input()
  selectLabel: string;

  @Input()
  infoText: string;

  @Input()
  warningText: string;

  @Input()
  optionTemplate?: TemplateRef<{ name: string; value: string }>;

  @Input()
  buttonIcon: SvgLibraryIcon;
  selectedOption: SelectOption;
  searchForm: UntypedFormGroup;
  filteredOptions: SelectOption[];
  dropDownStyle: { 'max-height': string };

  dropDownOpen$ = new BehaviorSubject<boolean>(false);
  activeIndex$ = new BehaviorSubject<number>(0);
  searchEnabled$ = new BehaviorSubject<boolean>(false);

  @ViewChild('dropDown')
  dropDown: ElementRef;

  @ViewChild('selectButton')
  selectButton: ElementRef;

  @ViewChild('buttonContainer')
  buttonContainer: ElementRef;

  @ViewChild('searchInput')
  searchInput: TextInputComponent;

  @ViewChild('optionsList')
  optionsList: ElementRef;

  @Output()
  dropDownOpen: EventEmitter<boolean> = new EventEmitter();

  @Output()
  inputEnterClickGtm: EventEmitter<void> = new EventEmitter();

  @HostListener('document:click', ['$event'])
  clickout(event: Event): void {
    if (
      this.dropDownOpen$.getValue() &&
      !this.dropDown.nativeElement.contains(event.target) &&
      !event.composedPath().includes(this.buttonContainer.nativeElement)
    ) {
      this.closeDropDown();
    }
  }

  constructor() {
    super();

    this.searchForm = this.fb.group({
      searchInput: this.fb.control(''),
    });
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.ctrlField.valueChanges.subscribe((value) => {
        if (!value) {
          this.selectedOption = undefined;
          this.activeIndex$.next(0);
        } else {
          this.selectOptionByValue(this.ctrlField.value);
        }
      })
    );

    // select initial value
    if (this.ctrlField.value) {
      this.selectOptionByValue(this.ctrlField.value);
    }

    this.subscriptions.add(
      this.searchForm.controls['searchInput'].valueChanges.subscribe((value) => {
        if (value && !this.dropDownOpen$.getValue()) {
          this.openDropDown(false);
        }
      })
    );

    this.subscriptions.add(
      this.activeIndex$.subscribe((activeIndex) => {
        this.optionsList?.nativeElement.children[activeIndex]?.scrollIntoView?.({
          block: 'nearest',
          behaviour: 'smooth',
        });
      })
    );
  }

  selectOptionByValue(value: string): void {
    this.selectedOption = this.options.find((option) => option.value === value);

    if (this.searchable) {
      this.searchForm.controls.searchInput.setValue(this.selectedOption.name);
    }

    this.activeIndex$.next(this.options.indexOf(this.selectedOption));
  }

  selectOption(option: SelectOption): void {
    this.ctrlField.setValue(option.value);
    this.closeDropDown();
  }

  selectCurrentActiveOption(): void {
    this.subscriptions.add(
      combineLatest([this.activeIndex$, this.dropDownOpen$])
        .pipe(
          filter(([_activeIndex, dropDownOpen]) => dropDownOpen),
          map(([activeIndex]) => activeIndex),
          take(1)
        )
        .subscribe((activeIndex) => {
          this.selectOption((this.filteredOptions || this.options)[activeIndex]);
        })
    );
  }

  closeDropDownOnFocusout(e: FocusEvent): void {
    if (this.dropDownOpen$.getValue() && !this.dropDown.nativeElement.contains(e.relatedTarget)) {
      this.closeDropDown();
    }
  }

  closeDropDown(): void {
    this.searchEnabled$.next(false);
    this.filteredOptions = undefined;

    this.searchForm.controls['searchInput'].setValue(this.selectedOption?.name || '');

    this.dropDownOpen$.next(false);
    this.dropDownOpen.emit(false);
  }

  openDropDown(clearInput = true): void {
    this.dropDownOpen$.next(true);

    this.filteredOptions = undefined;

    if (clearInput) {
      this.searchForm.controls.searchInput.setValue('');
    }

    if (this.searchable) {
      this.searchEnabled$.next(true);
    }

    this.dropDownOpen.emit(true);
  }

  onInputEnter(): void {
    this.inputEnterClickGtm?.emit();
    const dropDownOpen = this.dropDownOpen$.getValue();

    if (dropDownOpen) {
      this.selectCurrentActiveOption();
    } else {
      this.openDropDown();
    }
  }

  handleKeyDown(e: KeyboardEvent): void {
    const currentActiveIndex = this.activeIndex$.getValue();
    const opts = this.filteredOptions || this.options || [];
    const maxIndex = Math.max(0, opts.length - 1);

    const dropDownOpen = this.dropDownOpen$.getValue();
    switch (e.key) {
      case 'Escape':
        this.closeDropDown();
        break;
      case 'Down':
      case 'ArrowDown':
        stopPropagation(e);
        if (dropDownOpen) {
          this.activeIndex$.next(currentActiveIndex + 1 > maxIndex ? 0 : currentActiveIndex + 1);
        } else {
          this.openDropDown();
        }
        break;
      case 'Up':
      case 'ArrowUp':
        stopPropagation(e);
        if (dropDownOpen) {
          this.activeIndex$.next(currentActiveIndex - 1 < 0 ? maxIndex : currentActiveIndex - 1);
        } else {
          this.openDropDown();
          this.activeIndex$.next(maxIndex);
        }
        break;
    }
  }

  handleSearch(): void {
    const query = this.searchForm.controls['searchInput'].value;

    this.filteredOptions = this.options.filter((option) => option.name?.toLowerCase().startsWith(query.toLowerCase()));
  }

  clearSearch(e: Event): void {
    this.searchForm.controls.searchInput.setValue('');
    this.closeDropDown();
    this.searchInput?.inputElement.nativeElement.focus();
    e.stopPropagation();
  }

  protected readonly NotificationTheme = NotificationTheme;
}
